import { ReactNode } from "react"
import { Button, Popover, Stack } from "@mui/material"
import { ArrowDropDown as DropDownIcon } from '@mui/icons-material'
import { PlusIcon, SaveIcon, LoadIcon } from 'shared/icons'
import { usePopover } from "shared/popoverHooks"

import { SearchDetailResult } from "interface/search"
import { TargetListResult } from "interface/search/targetList"

import { DashBoardConditionChips } from "./dashBoardConditionChips"
import { GlobalAxisSelect } from "./globalAxisSelect"

import { LoadDashBoardProfilePopover } from "./popover/loadProfile"
import { SaveDashBoardProfilePopover } from "./popover/saveProfile"
import { EditDashBoardItem } from "./item/editForm"

import { DashBoardValue } from "../hooks"

import { MrpProjectSelect } from "features/project/mrp/components/mrpProjectSelect"
import { LvProjectSelect } from "features/project/lv/components/lvProjectSelect"

type DashBoardBarProps<T> = {
	search: SearchDetailResult | null
	targetList?: TargetListResult | null
	dashBoard: DashBoardValue<T>
	leftItems?: ReactNode,
	rightItems?: ReactNode
}
export const DashBoardBar = <T,>({
	search,
	targetList,
	dashBoard,
	leftItems,
	rightItems
}: DashBoardBarProps<T>) => {
	const {
		profileKey, configure,
		selectedProfile, onLoadProfile,
		onDeleteProfileReply,
		dashBoardItems,
		conditions, setConditions,
		hasGlobal, globalAxisKey, onChangeGlobalAxisKey,
		subSource: { mrp, lv }
	} = dashBoard

	const loadProfile = usePopover()
  const saveProfile = usePopover()
  const addItem = usePopover()

	return (
		<Stack direction="row" padding={1} spacing={1} alignItems="strech" flexWrap="wrap" justifyContent="flex-end" useFlexGap>
			<Stack direction="row" spacing={1} alignItems="strech" flexShrink={0}>
				{leftItems}
			</Stack>

			<DashBoardConditionChips
				flexGrow={1}
				justifyContent="flex-end"
				conditions={conditions}
				onDeleteCondition={(axisKey) => {
					const clone = { ...conditions }
					delete clone[axisKey]
					setConditions(clone)
				}}
			/>

			<Stack direction="row" spacing={1} alignItems="strech" flexShrink={0}>
				{hasGlobal && (
					<GlobalAxisSelect
						configure={configure}
						targetList={targetList}
						globalAxisKey={globalAxisKey}
						setGlobalAxisKey={onChangeGlobalAxisKey}
					/>
				)}

				{mrp.has && (
					<MrpProjectSelect
						projectsState={mrp.masterState}
						projectId={mrp.keyValue}
						setProjectId={mrp.setKeyValue}
						maxWidth="10em"
					/>
				)}

				{lv.has && (
					<LvProjectSelect
						projectsState={lv.masterState}
						projectCode={lv.keyValue}
						setProjectCode={lv.setKeyValue}
						maxWidth="10em"
					/>
				)}
			</Stack>

			<Stack direction="row" spacing={1} alignItems="strech" flexShrink={0}>
				<Button
					variant="outlined"
					size="medium"
					startIcon={<LoadIcon/>}
					endIcon={<DropDownIcon/>}
					sx={{ paddingRight: 1, whiteSpace: 'nowrap' }}
					onClick={(e) => loadProfile.openPopover(e.currentTarget)}                    
				>
					{selectedProfile?.profileName || "読込"}
				</Button>
				{loadProfile.popoverElement && (
					<LoadDashBoardProfilePopover
						anchorEl={loadProfile.popoverElement}
						profileKey={profileKey}
						search={search}
						onClick={(profile) => {
							onLoadProfile(profile)
							loadProfile.closePopover()
						}}
						onDeleteProfileReply={
							onDeleteProfileReply
						}
						onClose={loadProfile.closePopover}
					/>
				)}
				<Button
					variant="outlined"
					size="medium"
					startIcon={<SaveIcon/>}
					sx={{ whiteSpace: 'nowrap' }}
					onClick={(e) => saveProfile.openPopover(e.currentTarget)}
				>
					保存
				</Button>
				{saveProfile.popoverElement && (
					<SaveDashBoardProfilePopover
						anchorEl={saveProfile.popoverElement}
						profileKey={profileKey}
						search={search}
						dashBoard={dashBoard}
						onClose={() => saveProfile.closePopover()}
					/>
				)}

				<Button
					variant="outlined"
					size="medium"
					sx={{ whiteSpace: 'nowrap' }}
					startIcon={<PlusIcon />}
					onClick={(e) => addItem.openPopover(e.currentTarget)}
				>
					パネル追加
				</Button>
				{addItem.popoverElement && (
					<Popover
						id="new-item-dialog"
						open
						anchorEl={addItem.popoverElement}
						anchorOrigin={{
							vertical: 'bottom',
							horizontal: 'right',
						}}
						transformOrigin={{
							vertical: 'top',
							horizontal: 'right',
						}}
						onClose={() => addItem.closePopover()}
					>
						<EditDashBoardItem<T>
							isCreate
							itemData={{
								// default item data
								id: dashBoardItems.reduce((p, item) => Math.max(p, item.id), 0) + 1,
								width: 4,
								height: 4,
								x: 0, y: 0
							}}
							configure={configure}
							targetList={targetList ?? undefined}
							onSubmitForm={(itemData) => {
								dashBoard.addDashBoardItem(itemData)
								addItem.closePopover()
							}}
							onClose={addItem.closePopover}
						/>
					</Popover>
				)}
				{rightItems}
			</Stack>
		</Stack>
	)
}